// ============================================
// Biomarker Alias Resolver — HLX-CAP.02 D#4
//
// Single entry point for biomarker name resolution.
// Implements the three-tier resolution strategy:
//   1. Exact match (case-insensitive)
//   2. Fuzzy match (Levenshtein <=2, names >6 chars only)
//   3. Unidentified (flagged, logged, original preserved)
//
// Clinical neighbor exclusion prevents fuzzy matches
// between dangerous pairs (estradiol/estriol, T3/T4, ALT/AST).
//
// Multiple fuzzy matches → ambiguity protocol (never guesses).
// ============================================

import {
  resolveAmbiguity,
  buildCandidate,
  type DocumentContext,
  type SourceLocation,
  type SectionHeader,
  type SiblingBiomarker,
} from './ambiguityProtocol';

// ── Re-export the ALIAS_MAP from biomarkerAliasService for shared access ──
// We import the map and Levenshtein logic rather than duplicating.

// ── Canonical Alias Map ──
// Inlined reference to the same map used by biomarkerAliasService.
// This module imports at runtime to stay in sync.
import { ALIAS_MAP } from './biomarkerAliasService';

// ── Types ──

export interface AliasResolutionResult {
  canonicalName: string;            // Resolved ID or 'unidentified'
  originalName: string;             // Raw input preserved exactly
  matchType: 'exact' | 'fuzzy' | 'unidentified' | 'ambiguous';
  confidence: number;               // 1.0 for exact, 0-1 for fuzzy, 0 for unidentified
  processingFlags: string[];        // e.g. ['fuzzy_matched', 'clinical_neighbor_blocked']
  resolutionNotes: string;          // Human-readable audit trail
  candidateIds?: string[];          // Present when ambiguous — all candidates
}

export interface ResolverContext {
  sex?: 'Male' | 'Female';
  documentContext?: DocumentContext;
  sourceLocation?: SourceLocation;
  siblingBiomarkers?: SiblingBiomarker[];
  sectionHeaders?: SectionHeader[];
  extractedRange?: { low: number; high: number };
  extractedUnit?: string;
}

// ── Clinical Neighbor Exclusion List ──
// Fuzzy matching between these pairs is BLOCKED.
// Even if Levenshtein distance is <=2, these pairs must use exact alias match.
// Spec: "prevents fuzzy matches between estradiol/estriol, T3/T4, ALT/AST"

const CLINICAL_NEIGHBOR_EXCLUSION: Array<{ canonicalIds: string[]; namePatterns: string[] }> = [
  // Estradiol / Estriol — one letter difference, vastly different clinical meaning
  {
    canonicalIds: ['estradiol_female', 'estradiol_male', 'estriol'],
    namePatterns: ['estradiol', 'estriol', 'estradol'],
  },
  // T3 / T4 — different thyroid hormones
  {
    canonicalIds: ['free_t3', 'total_t3', 'free_t4', 'total_t4', 'reverse_t3'],
    namePatterns: ['t3', 't4', 'free t3', 'free t4', 'total t3', 'total t4'],
  },
  // ALT / AST — different liver enzymes
  {
    canonicalIds: ['alt', 'ast'],
    namePatterns: ['alt', 'ast', 'sgpt', 'sgot'],
  },
  // ALP / ALT — easily confused
  {
    canonicalIds: ['alp', 'alt'],
    namePatterns: ['alp', 'alt'],
  },
  // Estrone / Estradiol
  {
    canonicalIds: ['estrone', 'estradiol_female', 'estradiol_male'],
    namePatterns: ['estrone', 'estradiol'],
  },
];

/**
 * Check if two canonical IDs are clinical neighbors.
 * If so, fuzzy matching between them is forbidden.
 */
function areClinicalNeighbors(idA: string, idB: string): boolean {
  if (idA === idB) return false;
  for (const group of CLINICAL_NEIGHBOR_EXCLUSION) {
    if (group.canonicalIds.includes(idA) && group.canonicalIds.includes(idB)) {
      return true;
    }
  }
  return false;
}

/**
 * Check if a fuzzy match candidate would violate a clinical neighbor exclusion
 * given the full set of candidates at similar confidence.
 */
function hasClinicalNeighborConflict(candidateIds: string[]): boolean {
  for (let i = 0; i < candidateIds.length; i++) {
    for (let j = i + 1; j < candidateIds.length; j++) {
      if (areClinicalNeighbors(candidateIds[i], candidateIds[j])) {
        return true;
      }
    }
  }
  return false;
}

// ── Sex-Dimorphic Routing ──

const SEX_DIMORPHIC_MAP: Record<string, { Male: string; Female: string }> = {
  'estradiol_female': { Male: 'estradiol_male', Female: 'estradiol_female' },
  'progesterone_female': { Male: 'progesterone_male', Female: 'progesterone_female' },
  'fsh_female': { Male: 'fsh_male', Female: 'fsh_female' },
  'lh_female': { Male: 'lh_male', Female: 'lh_female' },
  'dheas_female': { Male: 'dheas_male', Female: 'dheas_female' },
  'shbg_female': { Male: 'shbg_male', Female: 'shbg_female' },
  'testosterone_total_male': { Male: 'testosterone_total_male', Female: 'testosterone_total_female' },
  'free_testosterone_male': { Male: 'free_testosterone_male', Female: 'free_testosterone_female' },
  'prolactin_male': { Male: 'prolactin_male', Female: 'prolactin_female' },
};

function applySexRouting(canonicalId: string, sex?: 'Male' | 'Female'): string {
  if (sex && SEX_DIMORPHIC_MAP[canonicalId]) {
    return SEX_DIMORPHIC_MAP[canonicalId][sex];
  }
  return canonicalId;
}

// ── Known Ambiguous Short Names ──
// These always fire the ambiguity protocol regardless of match type.

const AMBIGUOUS_NAMES: Record<string, string[]> = {
  't': ['total_t3', 'testosterone_total_male'],
  't3': ['free_t3', 'total_t3'],
  't4': ['free_t4', 'total_t4'],
};

// ── Levenshtein Distance ──

function levenshteinDistance(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));

  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = a[i - 1] === b[j - 1]
        ? dp[i - 1][j - 1]
        : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
    }
  }

  return dp[m][n];
}

// ── Unidentified Logging ──
// Accumulated during resolution for downstream log shipping.

const unidentifiedLog: Array<{
  originalName: string;
  timestamp: number;
  reason: string;
}> = [];

/**
 * Get and clear the unidentified biomarker log.
 * Called by upstream pipeline after processing a batch.
 */
export function drainUnidentifiedLog(): typeof unidentifiedLog {
  return unidentifiedLog.splice(0);
}

function logUnidentified(originalName: string, reason: string): void {
  unidentifiedLog.push({
    originalName,
    timestamp: Date.now(),
    reason,
  });
  // Also log to console for operational visibility
  console.warn(
    `[BiomarkerAliasResolver] Unidentified biomarker: "${originalName}" — ${reason}`,
  );
}

// ── Main Resolution Function ──

/**
 * Resolve a raw biomarker name to a canonical ID.
 *
 * Resolution tiers:
 *   1. Exact match (case-insensitive) against ALIAS_MAP → confidence 1.0
 *   2. Fuzzy match (Levenshtein <=2, name >6 chars only) → confidence 0.8-0.95
 *   3. Unidentified → confidence 0, original name preserved, logged
 *
 * Safety gates:
 *   - Clinical neighbor exclusion blocks fuzzy matches between dangerous pairs
 *   - Multiple fuzzy matches to different canonical IDs → ambiguity protocol
 *   - Known ambiguous short names (T, T3, T4) → ambiguity protocol
 */
export function resolveBiomarkerAlias(
  rawName: string,
  context?: ResolverContext,
): AliasResolutionResult {
  const normalized = rawName.toLowerCase().trim();

  // ── Check known ambiguous names FIRST ──
  const ambiguousCandidateIds = AMBIGUOUS_NAMES[normalized];
  if (ambiguousCandidateIds) {
    return resolveViaAmbiguityProtocol(rawName, normalized, ambiguousCandidateIds, context);
  }

  // ── Tier 1: Exact match (case-insensitive) ──
  const exactMatch = ALIAS_MAP[normalized];
  if (exactMatch) {
    const routed = applySexRouting(exactMatch, context?.sex);
    return {
      canonicalName: routed,
      originalName: rawName,
      matchType: 'exact',
      confidence: 1.0,
      processingFlags: [],
      resolutionNotes: `Exact match: "${rawName}" → "${routed}"`,
    };
  }

  // Also try stripping lab-specific numeric suffixes like "(3)", "(133)"
  const stripped = normalized.replace(/\(\d+\)\s*$/, '').trim();
  if (stripped !== normalized) {
    const strippedMatch = ALIAS_MAP[stripped];
    if (strippedMatch) {
      const routed = applySexRouting(strippedMatch, context?.sex);
      return {
        canonicalName: routed,
        originalName: rawName,
        matchType: 'exact',
        confidence: 1.0,
        processingFlags: [],
        resolutionNotes: `Exact match after suffix strip: "${rawName}" → "${routed}"`,
      };
    }
  }

  // ── Tier 2: Fuzzy match (Levenshtein <=2, names >6 chars only) ──
  // Spec: "Levenshtein <=2 for names >6 chars"
  // Short names (<=6 chars) are NOT fuzzy-matched — too risky.
  if (normalized.length > 6) {
    const fuzzyResult = fuzzyMatchWithExclusion(normalized, rawName, context?.sex);
    if (fuzzyResult) {
      return fuzzyResult;
    }
  }

  // ── Tier 3: Unidentified ──
  logUnidentified(rawName, 'No exact or fuzzy match found in alias table');

  return {
    canonicalName: 'unidentified',
    originalName: rawName,
    matchType: 'unidentified',
    confidence: 0,
    processingFlags: ['unidentified_biomarker'],
    resolutionNotes: `No match found for "${rawName}". Original name preserved. Logged for alias table review.`,
  };
}

// ── Fuzzy Match with Clinical Neighbor Exclusion ──

interface FuzzyCandidate {
  alias: string;
  canonicalId: string;
  distance: number;
}

function fuzzyMatchWithExclusion(
  normalized: string,
  rawName: string,
  sex?: 'Male' | 'Female',
): AliasResolutionResult | null {
  const MAX_DISTANCE = 2;
  const candidates: FuzzyCandidate[] = [];

  for (const [alias, canonicalId] of Object.entries(ALIAS_MAP)) {
    // Only fuzzy-match against aliases that are also >6 chars
    // (matching short aliases against long names produces false positives)
    if (alias.length <= 6) continue;

    const distance = levenshteinDistance(normalized, alias);
    if (distance > 0 && distance <= MAX_DISTANCE) {
      candidates.push({ alias, canonicalId, distance });
    }
  }

  if (candidates.length === 0) return null;

  // Sort by distance (best first), then alphabetically for determinism
  candidates.sort((a, b) => a.distance - b.distance || a.alias.localeCompare(b.alias));

  // Collect unique canonical IDs from candidates at the best distance
  const bestDistance = candidates[0].distance;
  const bestCandidates = candidates.filter((c) => c.distance === bestDistance);
  const uniqueIds = [...new Set(bestCandidates.map((c) => c.canonicalId))];

  // ── Clinical neighbor exclusion ──
  // If any pair of candidate IDs are clinical neighbors, block the fuzzy match entirely
  if (uniqueIds.length > 1 && hasClinicalNeighborConflict(uniqueIds)) {
    logUnidentified(
      rawName,
      `Fuzzy match blocked: clinical neighbor conflict between ${uniqueIds.join(', ')}`,
    );
    return {
      canonicalName: 'unidentified',
      originalName: rawName,
      matchType: 'unidentified',
      confidence: 0,
      processingFlags: ['unidentified_biomarker', 'clinical_neighbor_blocked'],
      resolutionNotes: `Fuzzy match for "${rawName}" blocked: candidates ${uniqueIds.join(', ')} are clinical neighbors. Requires exact alias match.`,
    };
  }

  // Even with a single best distance, check ALL candidates (not just best) for neighbor conflicts
  const allUniqueIds = [...new Set(candidates.map((c) => c.canonicalId))];
  if (allUniqueIds.length > 1 && hasClinicalNeighborConflict(allUniqueIds)) {
    // Best match may be safe, but close neighbors exist in the candidate pool
    // Check if the best candidate specifically conflicts with any other candidate
    const bestId = candidates[0].canonicalId;
    const conflictingNeighbor = allUniqueIds.find(
      (id) => id !== bestId && areClinicalNeighbors(bestId, id),
    );
    if (conflictingNeighbor) {
      logUnidentified(
        rawName,
        `Fuzzy match blocked: best match "${bestId}" is clinical neighbor of "${conflictingNeighbor}"`,
      );
      return {
        canonicalName: 'unidentified',
        originalName: rawName,
        matchType: 'unidentified',
        confidence: 0,
        processingFlags: ['unidentified_biomarker', 'clinical_neighbor_blocked'],
        resolutionNotes: `Fuzzy match for "${rawName}" blocked: "${bestId}" and "${conflictingNeighbor}" are clinical neighbors. Requires exact alias match.`,
      };
    }
  }

  // ── Multiple distinct IDs at same distance → ambiguity protocol ──
  if (uniqueIds.length > 1) {
    // [c4] Multiple matches trigger ambiguity protocol
    return {
      canonicalName: 'unidentified',
      originalName: rawName,
      matchType: 'ambiguous',
      confidence: 0,
      processingFlags: ['ambiguous', 'unidentified_biomarker'],
      candidateIds: uniqueIds,
      resolutionNotes: `Multiple fuzzy matches for "${rawName}" at distance ${bestDistance}: ${uniqueIds.join(', ')}. Ambiguity protocol triggered — never guesses.`,
    };
  }

  // ── Single unambiguous fuzzy match ──
  const winner = candidates[0];
  const routed = applySexRouting(winner.canonicalId, sex);
  // Confidence: distance 1 → 0.95, distance 2 → 0.85
  const confidence = winner.distance === 1 ? 0.95 : 0.85;

  return {
    canonicalName: routed,
    originalName: rawName,
    matchType: 'fuzzy',
    confidence,
    processingFlags: ['fuzzy_matched'],
    resolutionNotes: `Fuzzy match: "${rawName}" → "${routed}" (alias: "${winner.alias}", Levenshtein distance: ${winner.distance}, confidence: ${confidence})`,
  };
}

// ── Ambiguity Protocol Integration ──

function resolveViaAmbiguityProtocol(
  rawName: string,
  _normalized: string,
  candidateIds: string[],
  context?: ResolverContext,
): AliasResolutionResult {
  const candidates = candidateIds
    .map(buildCandidate)
    .filter((c): c is NonNullable<typeof c> => c !== null);

  if (candidates.length < 2) {
    // Shouldn't happen, but if it does, treat as unidentified
    logUnidentified(rawName, 'Ambiguous name but fewer than 2 valid candidates');
    return {
      canonicalName: 'unidentified',
      originalName: rawName,
      matchType: 'unidentified',
      confidence: 0,
      processingFlags: ['unidentified_biomarker'],
      resolutionNotes: `Ambiguous name "${rawName}" has fewer than 2 valid candidates.`,
    };
  }

  // If no context provided, we can't resolve — default to unidentified
  if (!context?.documentContext && !context?.siblingBiomarkers && !context?.sectionHeaders) {
    logUnidentified(rawName, 'Ambiguous name with no document context for resolution');
    return {
      canonicalName: 'unidentified',
      originalName: rawName,
      matchType: 'ambiguous',
      confidence: 0,
      processingFlags: ['ambiguous', 'unidentified_biomarker'],
      candidateIds,
      resolutionNotes: `Ambiguous name "${rawName}" matches ${candidateIds.join(', ')}. No document context available — defaulting to unidentified. Never guesses.`,
    };
  }

  // Delegate to ambiguity protocol
  const docCtx: DocumentContext = context?.documentContext ?? {
    sectionHeaders: context?.sectionHeaders ?? [],
    siblingBiomarkers: context?.siblingBiomarkers ?? [],
  };

  const loc: SourceLocation = context?.sourceLocation ?? { page: 1, x: 0, y: 0 };

  const result = resolveAmbiguity({
    rawName,
    candidates,
    sourceLocation: loc,
    extractedRange: context?.extractedRange,
    extractedUnit: context?.extractedUnit,
    documentContext: docCtx,
  });

  if (result.resolved && result.canonicalId) {
    const routed = applySexRouting(result.canonicalId, context?.sex);
    return {
      canonicalName: routed,
      originalName: rawName,
      matchType: 'exact', // Context-resolved ambiguity is treated as resolved
      confidence: 0.9,
      processingFlags: [...result.processingFlags, 'context_resolved'],
      resolutionNotes: result.resolutionNotes,
    };
  }

  // Unresolved — never guess
  logUnidentified(rawName, result.resolutionNotes);
  return {
    canonicalName: 'unidentified',
    originalName: rawName,
    matchType: 'ambiguous',
    confidence: 0,
    processingFlags: result.processingFlags,
    candidateIds,
    resolutionNotes: result.resolutionNotes,
  };
}

// ── Batch Resolution ──

/**
 * Resolve an array of raw biomarker names.
 * Returns results in the same order as input.
 */
export function resolveBiomarkerAliases(
  rawNames: string[],
  context?: ResolverContext,
): AliasResolutionResult[] {
  return rawNames.map((name) => resolveBiomarkerAlias(name, context));
}

// ── Exports for testing / inspection ──

export { areClinicalNeighbors, hasClinicalNeighborConflict, levenshteinDistance };
export { CLINICAL_NEIGHBOR_EXCLUSION };
